import type { ApolloClient } from '@apollo/client';
import type { NotificationStoreActionsType } from '~/components/NotificationTray/NotificationStore';
import { paths } from '~/singlePageApp/paths';
import { noOpFn } from '~/utilities/util';

const HR_IN_DAY = 24;
const MIN_IN_HR = 60;
const S_IN_MIN = 60;
const MS_IN_HR = S_IN_MIN * MIN_IN_HR * 1000;
const MIN_IN_DAY = MIN_IN_HR * HR_IN_DAY;
const MS_IN_DAY = S_IN_MIN * MIN_IN_DAY * 1000;

type UpdateCacheFunction =
  | ((client: ApolloClient<unknown>) => Promise<unknown>)
  | ((client: ApolloClient<unknown>) => void);

const addBulkActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  inProgressTitle,
  doneTitle,
  updateCache,
  subtitleAction,
  subtitleLink,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  inProgressTitle: string;
  doneTitle: string;
  updateCache: UpdateCacheFunction;
  subtitleAction: string;
  subtitleLink?: { label?: string; href?: string };
}): void => {
  addTaskToQueue(
    {
      id: backgroundJobStatusId,
      type: 'BULK_ACTION',
      data: {
        backgroundJobStatusId,
        inProgressTitle,
        doneTitle,
        updateCache,
        subtitleAction,
        subtitleLink,
      },
      TTL_MS: MS_IN_HR,
    },
    true,
  );
};

export const addArchiveActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
  updateCache,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
}): void => {
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `Archiving ${selectionCount} media`,
    doneTitle: `Archived COUNT media`,
    updateCache,
    subtitleAction: 'media',
    subtitleLink: { label: 'archived', href: paths.archived() },
  });
};

export const addRestoreActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
  updateCache,
  containerTitle,
  containerPath,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
  containerTitle: string;
  containerPath: string;
}): void => {
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `Restoring ${selectionCount} media`,
    doneTitle: `Restored COUNT media`,
    updateCache,
    subtitleAction: 'media restored to',
    subtitleLink: { label: containerTitle, href: containerPath },
  });
};

export const addDeleteActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
  updateCache,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
}): void => {
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `Deleting ${selectionCount} media`,
    doneTitle: `Deleted COUNT media`,
    updateCache,
    subtitleAction: 'media deleted',
  });
};

export const addMoveOrCopyActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  action,
  selectionCount,
  updateCache,
  containerTitle,
  containerPath,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  action: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
  containerTitle?: string;
  containerPath?: string;
}): void => {
  const inProgressAction = action === 'move' ? 'Moving' : 'Copying';
  const doneAction = action === 'move' ? 'Moved' : 'Copied';
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `${inProgressAction} ${selectionCount} media`,
    doneTitle: `${doneAction} COUNT media`,
    updateCache,
    subtitleAction: `media ${doneAction.toLowerCase()} to`,
    subtitleLink: {
      label: containerTitle,
      href: containerPath,
    },
  });
};

export const addTagActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
  updateCache,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
}): void => {
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `Tagging ${selectionCount} media`,
    doneTitle: `Tagged COUNT media`,
    updateCache,
    subtitleAction: `media tagged`,
  });
};

export const addPublishOrUnpublishActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  action,
  selectionCount,
  updateCache,
  channelTitle,
  channelPath,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  action: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
  channelTitle: string;
  channelPath: string;
}): void => {
  const inProgressAction = action === 'publish' ? 'Publishing' : 'Unpublishing';
  const doneAction = action === 'publish' ? 'Published' : 'Unpublished';
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `${inProgressAction} ${selectionCount} media`,
    doneTitle: `${doneAction} COUNT media`,
    updateCache,
    subtitleAction: `media ${doneAction.toLowerCase()} in`,
    subtitleLink: {
      label: channelTitle,
      href: channelPath,
    },
  });
};

export const addDownloadActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
}): void => {
  addTaskToQueue(
    {
      id: backgroundJobStatusId,
      type: 'BULK_DOWNLOAD',
      data: {
        backgroundJobStatusId,
        inProgressTitle: `Getting ${selectionCount} media ready for download`,
        doneTitle: `${selectionCount} media ready for download`,
      },
      TTL_MS: MS_IN_DAY,
    },
    true,
  );
};

export const addSetCommentsActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
}): void => {
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `Setting comments for ${selectionCount} media`,
    doneTitle: `Set comments for COUNT media`,
    subtitleAction: `media have updated comment settings`,
    updateCache: noOpFn,
  });
};

export const addAddToChannelActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
  updateCache,
  containerTitle,
  containerPath,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
  containerTitle: string;
  containerPath: string;
}): void => {
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `Adding ${selectionCount} media to Channel`,
    doneTitle: `Added COUNT media to Channel`,
    updateCache,
    subtitleAction: 'media added to',
    subtitleLink: { label: containerTitle, href: containerPath },
  });
};

export const addRemoveFromChannelActionToNotificationTray = ({
  addTaskToQueue,
  backgroundJobStatusId,
  selectionCount,
  updateCache,
  channelTitle,
  channelPath,
}: {
  addTaskToQueue: NotificationStoreActionsType['addTaskToQueue'];
  backgroundJobStatusId: string;
  selectionCount: number;
  updateCache: UpdateCacheFunction;
  channelTitle: string;
  channelPath: string;
}): void => {
  addBulkActionToNotificationTray({
    addTaskToQueue,
    backgroundJobStatusId,
    inProgressTitle: `Removing ${selectionCount} media from Channel`,
    doneTitle: `Removed COUNT media from Channel`,
    updateCache,
    subtitleAction: 'media removed from',
    subtitleLink: { label: channelTitle, href: channelPath },
  });
};
